"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  Info,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Wallet,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  BRUT_ASGARI_UCRET,
  SGK_TAVAN,
  YILI,
  hesaplaMaas,
  formatTL,
  type MaasSonuc,
} from "./maas-engine";

const howToSchema = generateHowToSchema({
  title: "Brüt Maaştan Net Maaş Nasıl Hesaplanır?",
  description:
    "2026 güncel vergi dilimleriyle brüt maaştan SGK, gelir vergisi, damga vergisi kesintilerini ve net maaşı hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Brüt Maaşı Girin",
      text: "Aylık brüt maaşınızı TL cinsinden girin.",
    },
    {
      name: "Ayı Seçin",
      text: "Kümülatif gelir vergisi dilimi için hangi ay olduğunu seçin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "Net maaş, SGK kesintileri, gelir vergisi, damga vergisi ve işveren maliyetini görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

const AY_ISIMLERI = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

export default function MaasHesaplayiciClient() {
  const [brutMaas, setBrutMaas] = useState("");
  const [ay, setAy] = useState(new Date().getMonth() + 1);
  const [sonuc, setSonuc] = useState<MaasSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(brutMaas);

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      return;
    }

    const hesapSonuc = hesaplaMaas(maas, ay);
    setSonuc(hesapSonuc);
  }, [brutMaas, ay]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Maaş Hesaplayıcı",
                  href: "/araclar/maas-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Wallet className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Net Maaş Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Güncel Vergi Dilimleri
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Brüt maaştan net maaşı, SGK kesintilerini, gelir vergisini ve
              işveren maliyetini hesaplayın. {YILI} kümülatif vergi dilimleri
              dahil.
            </p>
          </div>
        </section>

        {/* Hesaplama */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Bilgi kutusu */}
              <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">
                  {YILI} Asgari Ücret (Ocak–Haziran)
                </h2>
                <span className="text-2xl font-bold text-blue-600">
                  {formatTL(BRUT_ASGARI_UCRET)}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  SGK Tavan: {formatTL(SGK_TAVAN)}
                </p>
              </div>

              {/* Girdiler */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="brutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Brüt Maaş
                  </label>
                  <div className="relative">
                    <input
                      id="brutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={brutMaas}
                      onChange={(e) => setBrutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <div className="flex gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setBrutMaas(BRUT_ASGARI_UCRET.toString())}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {YILI} Asgari Ücret ({formatTL(BRUT_ASGARI_UCRET)})
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="ay"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Hesaplama Ayı
                  </label>
                  <select
                    id="ay"
                    value={ay}
                    onChange={(e) => setAy(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                  >
                    {AY_ISIMLERI.map((isim, i) => (
                      <option key={isim} value={i + 1}>
                        {isim}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Kümülatif gelir vergisi dilimine etkili
                  </p>
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {/* Sonuç */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {sonuc.sgkTavanUygulandi && (
                  <div className="mb-4 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      <strong>SGK Tavan Uyarısı:</strong> Brüt maaşınız{" "}
                      {formatTL(SGK_TAVAN)} SGK tavanını aştığı için SGK
                      primleri tavan üzerinden hesaplanmıştır.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  <div className="bg-blue-600 text-white rounded-xl p-4">
                    <span className="text-xs text-blue-100 block">
                      Net Maaş
                    </span>
                    <span className="text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(sonuc.netMaas)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Brüt Maaş
                    </span>
                    <span className="text-lg font-bold text-gray-900 mt-1 block tabular-nums">
                      {formatTL(sonuc.brutMaas)}
                    </span>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <span className="text-xs text-indigo-600 block">
                      İşveren Maliyeti
                    </span>
                    <span className="text-lg font-bold text-indigo-700 mt-1 block tabular-nums">
                      {formatTL(sonuc.toplamIsverenMaliyeti)}
                    </span>
                  </div>
                </div>

                {/* İşçi Kesintileri */}
                <div className="bg-white rounded-xl p-5 border border-gray-200 mb-4">
                  <h3 className="font-bold text-gray-900 mb-3">
                    İşçi Kesintileri
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        SGK İşçi Primi (%14)
                      </span>
                      <span className="font-medium text-red-500 tabular-nums">
                        -{formatTL(sonuc.sgkIsciPrimi)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        İşsizlik Sigortası (%1)
                      </span>
                      <span className="font-medium text-red-500 tabular-nums">
                        -{formatTL(sonuc.issizlikIsciPrimi)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Gelir Vergisi ({AY_ISIMLERI[ay - 1]})
                      </span>
                      <span className="font-medium text-red-500 tabular-nums">
                        -{formatTL(sonuc.gelirVergisi)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Damga Vergisi (%0,759)
                      </span>
                      <span className="font-medium text-red-500 tabular-nums">
                        -{formatTL(sonuc.damgaVergisi)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span className="text-gray-900">Toplam Kesinti</span>
                      <span className="text-red-600 tabular-nums">
                        -{formatTL(sonuc.toplamKesinti)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* İşveren Maliyeti */}
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    İşveren Maliyeti
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Brüt Maaş</span>
                      <span className="font-medium tabular-nums">
                        {formatTL(sonuc.brutMaas)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        SGK İşveren Payı (%20,5)
                      </span>
                      <span className="font-medium text-orange-500 tabular-nums">
                        +{formatTL(sonuc.sgkIsverenPrimi)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        İşsizlik İşveren Payı (%2)
                      </span>
                      <span className="font-medium text-orange-500 tabular-nums">
                        +{formatTL(sonuc.issizlikIsverenPrimi)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span className="text-gray-900">
                        Toplam İşveren Maliyeti
                      </span>
                      <span className="text-indigo-600 tabular-nums">
                        {formatTL(sonuc.toplamIsverenMaliyeti)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      İşveren ek maliyet oranı: %
                      {sonuc.isverenEkMaliyetOrani.toFixed(1)}
                    </p>
                  </div>
                </div>

                {/* Net Oran Bar */}
                <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Net / Brüt Oranı</span>
                    <span className="font-bold text-blue-600">
                      %{sonuc.netOrani.toFixed(1)}
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{ width: `${sonuc.netOrani}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SSS */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Maaş Hesaplama Rehberi ({YILI})
          </h2>
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Sık Sorulan Sorular
              </h3>
              <div className="space-y-3">
                <SSSSoru
                  soru="Brüt maaş ile net maaş arasındaki fark nedir?"
                  cevap="Brüt maaş, tüm kesintiler yapılmadan önceki toplam maaştır. Net maaş ise SGK primi, işsizlik sigortası, gelir vergisi ve damga vergisi kesildikten sonra elinize geçen tutardır."
                />
                <SSSSoru
                  soru="Gelir vergisi neden her ay değişir?"
                  cevap="Türkiye'de gelir vergisi kümülatif (artan oranlı) olarak hesaplanır. Yıl başında %15 olan oran, yıllık gelir vergisi matrahı arttıkça %20, %27, %35 ve %40'a kadar çıkabilir. Bu yüzden yılın son aylarında net maaşınız düşer."
                />
                <SSSSoru
                  soru="SGK tavanı ne anlama gelir?"
                  cevap={`${YILI} yılında SGK primine esas kazanç tavanı ${formatTL(SGK_TAVAN)}'dir. Brüt maaşınız bu tutarı aşsa bile SGK primleri tavan üzerinden hesaplanır. Bu durum yüksek maaşlılarda net maaşı artırır.`}
                />
                <SSSSoru
                  soru="İşveren maliyeti nedir?"
                  cevap="İşveren maliyeti, çalışana ödenen brüt maaşın üzerine SGK işveren payı (%20,5) ve işsizlik sigortası işveren payı (%2) eklenerek hesaplanır. Yani bir çalışanın işverene toplam maliyeti, brüt maaşın yaklaşık %22,5 üzerindedir."
                />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                {YILI} yılı Ocak–Haziran dönemi oranları kullanılmıştır.
                Kümülatif vergi dilimi seçilen aya göre hesaplanır. Kesin
                hesaplama için mali müşavirinize danışınız.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="maas-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-blue-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Bordro Süreçlerinizi Otomatikleştirin
            </h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              IKAI ile maaş hesaplama, bordro yönetimi ve tüm İK süreçlerinizi
              tek platformda yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
            >
              Ücretsiz Başlayın
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
